export class ElenweaveNavigator {
  constructor(view, options = {}) {
    if (!view) throw new Error('ElenweaveNavigator requires a view');
    this.view = view;
    this.keymap = options.keymap || DEFAULT_KEYMAP;
    this._boundKeydown = (e) => this.handleKey(e);
    this._ui = null;
    this._uiHandlers = [];
    this._uiEnabled = false;
    if (options.ui !== false) {
      this.enableUI(options.uiOptions || {});
    }
  }

  bindKeyboard(element) {
    const target = element || this.view.getContainer();
    if (!target) return;
    target.addEventListener('keydown', this._boundKeydown);
  }

  unbindKeyboard(element) {
    const target = element || this.view.getContainer();
    if (!target) return;
    target.removeEventListener('keydown', this._boundKeydown);
  }

  handleKey(e) {
    if (!this.view.isFocused()) return;
    if (isEditableTarget(e.target)) return;
    const key = toKeyId(e);
    const action = this.keymap[key];
    if (!action) return;
    if (action.preventDefault !== false) e.preventDefault();

    switch (action.type) {
      case 'nav:dir':
        if (!this._hasSelection()) {
          this._panDirection(action.dir, e.shiftKey ? 2 : 1);
        } else {
          this.selectInDirection(action.dir);
        }
        break;
      case 'nav:next':
        this.selectNext();
        break;
      case 'nav:prev':
        this.selectPrev();
        break;
      case 'nav:connected-next':
        this.selectConnectedNext();
        break;
      case 'nav:connected-prev':
        this.selectConnectedPrev();
        break;
      case 'zoom:in':
        this.zoomBy(1.1);
        break;
      case 'zoom:out':
        this.zoomBy(0.9);
        break;
      case 'fit':
        this.fitToNodes();
        break;
      case 'focus':
        this.focusSelection();
        break;
      case 'center':
        this.centerOnSelection();
        break;
      default:
        break;
    }
  }

  enableUI(options = {}) {
    if (this._uiEnabled) return;
    const container = this.view.getContainer();
    if (!container) return;
    injectStyles();

    const ui = document.createElement('div');
    ui.className = 'ew-nav-ui';
    ui.setAttribute('role', 'toolbar');
    ui.setAttribute('aria-label', 'Navigation');
    ui.addEventListener('pointerdown', (e) => e.stopPropagation());

    const clusterPad = document.createElement('div');
    clusterPad.className = 'ew-nav-cluster-pad';
    if (options.includeZoom !== false) {
      clusterPad.append(
        this._makeButton('Zoom In', 'zoom-in', () => this.zoomBy(1.1), {
          emphasis: 'primary',
          pos: 'zoom-in'
        }),
        this._makeButton('Fit to Content', 'fit', () => this.fitToNodes(), {
          emphasis: 'primary',
          pos: 'fit'
        }),
        this._makeButton('Zoom Out', 'zoom-out', () => this.zoomBy(0.9), {
          emphasis: 'primary',
          pos: 'zoom-out'
        })
      );
    }
    if (options.includeCycle !== false) {
      clusterPad.append(
        this._makeButton('Previous', 'chevron-left', () => this.selectPrev(), {
          emphasis: 'tertiary',
          pos: 'prev'
        }),
        this._makeButton('Next', 'chevron-right', () => this.selectNext(), {
          emphasis: 'tertiary',
          pos: 'next'
        })
      );
    }
    if (options.includeFocus === true) {
      clusterPad.append(
        this._makeButton('Focus Selection', 'focus', () => this.focusSelection(), {
          emphasis: 'secondary',
          pos: 'focus'
        })
      );
    }

    const padGroup = document.createElement('div');
    padGroup.className = 'ew-nav-group';
    const pad = document.createElement('div');
    pad.className = 'ew-nav-pad';
    pad.append(
      this._makeButton('Pan Up', 'arrow-up', () => this._handleDirectional('up'), { emphasis: 'primary' }),
      this._makeButton('Pan Left', 'arrow-left', () => this._handleDirectional('left'), { emphasis: 'primary' }),
      this._makeButton('Center View', 'center', () => this.centerOnSelection(), { emphasis: 'secondary' }),
      this._makeButton('Pan Right', 'arrow-right', () => this._handleDirectional('right'), { emphasis: 'primary' }),
      this._makeButton('Pan Down', 'arrow-down', () => this._handleDirectional('down'), { emphasis: 'primary' })
    );
    padGroup.appendChild(pad);

    const clusterPanel = document.createElement('div');
    clusterPanel.className = 'ew-nav-cluster-panel';
    if (clusterPad.childElementCount > 0) clusterPanel.appendChild(clusterPad);

    const padPanel = document.createElement('div');
    padPanel.className = 'ew-nav-pad-panel';
    if (options.includeDirectional !== false) padPanel.appendChild(padGroup);

    if (clusterPanel.childElementCount > 0) ui.appendChild(clusterPanel);
    if (padPanel.childElementCount > 0) ui.appendChild(padPanel);
    container.appendChild(ui);
    this._ui = ui;
    this._uiEnabled = true;
  }

  disableUI() {
    if (!this._uiEnabled) return;
    this._uiHandlers.forEach(({ el, handler }) => el.removeEventListener('click', handler));
    this._uiHandlers = [];
    if (this._ui?.parentElement) this._ui.parentElement.removeChild(this._ui);
    this._ui = null;
    this._uiEnabled = false;
  }

  _makeButton(label, iconName, handler, options = {}) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ew-nav-btn';
    if (options.emphasis) btn.classList.add(`ew-nav-btn--${options.emphasis}`);
    if (options.pos) btn.dataset.pos = options.pos;
    btn.title = label;
    btn.setAttribute('aria-label', label);
    const icon = createIcon(iconName);
    if (icon) btn.appendChild(icon);
    const clickHandler = () => {
      handler();
      btn.classList.remove('ew-nav-btn--pulse');
      void btn.offsetWidth;
      btn.classList.add('ew-nav-btn--pulse');
      window.setTimeout(() => btn.classList.remove('ew-nav-btn--pulse'), 220);
    };
    btn.addEventListener('click', clickHandler);
    this._uiHandlers.push({ el: btn, handler: clickHandler });
    return btn;
  }

  panBy(dx, dy) {
    this.view.camera.x += dx;
    this.view.camera.y += dy;
    this.view.invalidate();
  }

  zoomBy(factor, anchor) {
    const rect = this.view.canvas.getBoundingClientRect();
    const center = anchor || { x: rect.width / 2, y: rect.height / 2 };
    const prev = this.view.camera.s;
    const next = clamp(prev * factor, this.view.minZoom, this.view.maxZoom);
    const scale = next / prev;
    this.view.camera.s = next;
    this.view.camera.x = center.x - (center.x - this.view.camera.x) * scale;
    this.view.camera.y = center.y - (center.y - this.view.camera.y) * scale;
    this.view.invalidate();
  }

  zoomTo(scale, anchor) {
    const rect = this.view.canvas.getBoundingClientRect();
    const center = anchor || { x: rect.width / 2, y: rect.height / 2 };
    const prev = this.view.camera.s;
    const next = clamp(scale, this.view.minZoom, this.view.maxZoom);
    const scaleFactor = next / prev;
    this.view.camera.s = next;
    this.view.camera.x = center.x - (center.x - this.view.camera.x) * scaleFactor;
    this.view.camera.y = center.y - (center.y - this.view.camera.y) * scaleFactor;
    this.view.invalidate();
  }

  focusSelection() {
    const node = this._getSelectedNode();
    if (node) this.view.focusNode(node.id);
  }

  centerOnSelection() {
    const node = this._getSelectedNode();
    if (!node) return;
    const rect = this.view.canvas.getBoundingClientRect();
    this.view.camera.x = rect.width / 2 - (node.x + node.w / 2) * this.view.camera.s;
    this.view.camera.y = rect.height / 2 - (node.y + node.h / 2) * this.view.camera.s;
    this.view.invalidate();
  }

  fitToNodes(nodeIds = null, padding = 64) {
    const graph = this.view.graph;
    if (!graph || !graph.nodes.length) return;
    const nodes = nodeIds
      ? graph.nodes.filter((n) => nodeIds.includes(n.id))
      : graph.nodes;
    if (!nodes.length) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    nodes.forEach((n) => {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.w);
      maxY = Math.max(maxY, n.y + n.h);
    });
    const rect = this.view.canvas.getBoundingClientRect();
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    const scale = Math.min(
      (rect.width - padding * 2) / width,
      (rect.height - padding * 2) / height
    );
    this.view.camera.s = clamp(scale, this.view.minZoom, this.view.maxZoom);
    this.view.camera.x = rect.width / 2 - (minX + width / 2) * this.view.camera.s;
    this.view.camera.y = rect.height / 2 - (minY + height / 2) * this.view.camera.s;
    this.view.invalidate();
  }

  selectNext() {
    const graph = this.view.graph;
    if (!graph || !graph.nodes.length) return;
    const currentId = this.view.state.selectedId;
    const idx = graph.nodes.findIndex((n) => n.id === currentId);
    const next = graph.nodes[(idx + 1) % graph.nodes.length];
    this.view.selectNode(next.id);
  }

  selectPrev() {
    const graph = this.view.graph;
    if (!graph || !graph.nodes.length) return;
    const currentId = this.view.state.selectedId;
    const idx = graph.nodes.findIndex((n) => n.id === currentId);
    const prev = graph.nodes[(idx - 1 + graph.nodes.length) % graph.nodes.length];
    this.view.selectNode(prev.id);
  }

  selectConnectedNext() {
    this._selectNeighbor(1);
  }

  selectConnectedPrev() {
    this._selectNeighbor(-1);
  }

  selectInDirection(dir) {
    const graph = this.view.graph;
    if (!graph || !graph.nodes.length) return;
    const current = this._getSelectedNode();
    if (!current) {
      this.view.selectNode(graph.nodes[0].id);
      return;
    }

    const direction = DIR_VECTORS[dir];
    if (!direction) return;
    const neighbors = this._neighbors(current.id);
    const pickedNeighbor = pickDirectional(neighbors, current, direction);
    if (pickedNeighbor) {
      this.view.selectNode(pickedNeighbor.id);
      this._neighborCursor?.set(current.id, neighbors.findIndex((n) => n.id === pickedNeighbor.id));
      return;
    }

    const fallback = pickDirectional(graph.nodes.filter((n) => n.id !== current.id), current, direction);
    if (fallback) this.view.selectNode(fallback.id);
  }

  _handleDirectional(dir) {
    if (!this._hasSelection()) {
      this._panDirection(dir, 1);
      return;
    }
    this.selectInDirection(dir);
  }

  _hasSelection() {
    return Boolean(this.view.state.selectedId || this.view.state.selectedEdgeId);
  }

  _panDirection(dir, multiplier = 1) {
    const vector = DIR_VECTORS[dir];
    if (!vector) return;
    const step = 60 * multiplier;
    this.panBy(vector.x * step, vector.y * step);
  }

  _selectNeighbor(step) {
    const current = this._getSelectedNode();
    if (!current) return;
    const neighbors = this._neighbors(current.id);
    if (!neighbors.length) return;
    this._neighborCursor = this._neighborCursor || new Map();
    const prev = this._neighborCursor.get(current.id) ?? 0;
    const nextIndex = (prev + step + neighbors.length) % neighbors.length;
    this._neighborCursor.set(current.id, nextIndex);
    const next = neighbors[nextIndex];
    if (next) this.view.selectNode(next.id);
  }

  _neighbors(nodeId) {
    const graph = this.view.graph;
    if (!graph) return [];
    const set = new Map();
    graph.edges.forEach((e) => {
      if (e.from === nodeId) set.set(e.to, graph.getNode(e.to));
      if (e.to === nodeId) set.set(e.from, graph.getNode(e.from));
    });
    return Array.from(set.values()).filter(Boolean);
  }

  _getSelectedNode() {
    const graph = this.view.graph;
    if (!graph) return null;
    const id = this.view.state.selectedId;
    if (id) return graph.getNode(id);
    if (this.view.state.selectedEdgeId) {
      const edge = graph.edges.find((e) => e.id === this.view.state.selectedEdgeId);
      if (edge) return graph.getNode(edge.from) || graph.getNode(edge.to);
    }
    return null;
  }
}

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
.ew-nav-ui {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
}
.ew-nav-cluster-panel,
.ew-nav-pad-panel {
  position: absolute;
  bottom: 16px;
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 18px;
  background: transparent;
  border: none;
  box-shadow: none;
  pointer-events: auto;
}
.ew-nav-cluster-panel {
  left: 16px;
}
.ew-nav-pad-panel {
  right: 16px;
}
.ew-nav-cluster-pad {
  display: grid;
  grid-template-columns: repeat(3, 38px);
  grid-template-rows: repeat(3, 38px);
  gap: 6px;
  place-items: center;
}
.ew-nav-cluster-pad .ew-nav-btn[data-pos="zoom-in"] { grid-column: 2; grid-row: 1; }
.ew-nav-cluster-pad .ew-nav-btn[data-pos="zoom-out"] { grid-column: 2; grid-row: 3; }
.ew-nav-cluster-pad .ew-nav-btn[data-pos="fit"] { grid-column: 2; grid-row: 2; }
.ew-nav-cluster-pad .ew-nav-btn[data-pos="prev"] { grid-column: 1; grid-row: 2; }
.ew-nav-cluster-pad .ew-nav-btn[data-pos="next"] { grid-column: 3; grid-row: 2; }
.ew-nav-cluster-pad .ew-nav-btn[data-pos="focus"] { grid-column: 1; grid-row: 1; }
.ew-nav-group {
  display: grid;
  gap: 6px;
}
.ew-nav-divider {
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, transparent, var(--ew-nav-border, rgba(95, 234, 255, 0.35)), transparent);
}
.ew-nav-pad {
  display: grid;
  grid-template-columns: repeat(3, 36px);
  grid-template-rows: repeat(3, 36px);
  gap: 6px;
  place-items: center;
}
.ew-nav-pad .ew-nav-btn:nth-child(1) { grid-column: 2; grid-row: 1; }
.ew-nav-pad .ew-nav-btn:nth-child(2) { grid-column: 1; grid-row: 2; }
.ew-nav-pad .ew-nav-btn:nth-child(3) { grid-column: 2; grid-row: 2; }
.ew-nav-pad .ew-nav-btn:nth-child(4) { grid-column: 3; grid-row: 2; }
.ew-nav-pad .ew-nav-btn:nth-child(5) { grid-column: 2; grid-row: 3; }
.ew-nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid var(--ew-nav-border, rgba(95, 234, 255, 0.35));
  background: var(--ew-nav-bg, rgba(14, 36, 52, 0.92));
  color: var(--ew-nav-text, rgba(226, 246, 255, 0.88));
  cursor: pointer;
  box-shadow: inset 0 0 8px var(--ew-nav-glow, rgba(95, 234, 255, 0.16)), 0 6px 12px rgba(0, 0, 0, 0.25);
  display: grid;
  place-items: center;
  transition: transform 120ms ease-out, box-shadow 120ms ease-out, border-color 120ms ease-out, color 120ms ease-out;
}
.ew-nav-btn:hover {
  border-color: var(--ew-nav-hover, rgba(95, 234, 255, 0.7));
  color: var(--ew-nav-text, rgba(198, 250, 255, 0.98));
  transform: translateY(-1px);
  box-shadow: inset 0 0 12px var(--ew-nav-glow, rgba(95, 234, 255, 0.22)), 0 10px 16px rgba(0, 0, 0, 0.28);
}
.ew-nav-btn:active {
  transform: translateY(1px);
  box-shadow: inset 0 0 14px var(--ew-nav-glow, rgba(95, 234, 255, 0.24)), 0 4px 8px rgba(0, 0, 0, 0.2);
  border-color: var(--ew-nav-border, rgba(95, 234, 255, 0.45));
}
.ew-nav-btn--primary {
  width: 38px;
  height: 38px;
}
.ew-nav-btn--secondary {
  border-color: var(--ew-nav-border, rgba(95, 234, 255, 0.28));
  background: var(--ew-nav-bg, rgba(10, 30, 44, 0.9));
}
.ew-nav-btn--tertiary {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border-color: var(--ew-nav-border, rgba(95, 234, 255, 0.22));
  background: var(--ew-nav-bg, rgba(9, 24, 36, 0.88));
  box-shadow: inset 0 0 6px var(--ew-nav-glow, rgba(95, 234, 255, 0.12)), 0 4px 8px rgba(0, 0, 0, 0.18);
}
.ew-nav-btn--tertiary:hover {
  border-color: var(--ew-nav-hover, rgba(95, 234, 255, 0.45));
  transform: translateY(-1px);
}
.ew-nav-btn svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ew-nav-btn--primary svg {
  width: 18px;
  height: 18px;
}
.ew-nav-btn--pulse {
  animation: ew-nav-pulse 180ms ease-out;
}
@keyframes ew-nav-pulse {
  0% { box-shadow: inset 0 0 8px var(--ew-nav-glow, rgba(95, 234, 255, 0.18)), 0 6px 12px rgba(0,0,0,0.25); }
  50% { box-shadow: inset 0 0 16px var(--ew-nav-glow, rgba(95, 234, 255, 0.32)), 0 0 14px var(--ew-nav-glow, rgba(95, 234, 255, 0.18)); }
  100% { box-shadow: inset 0 0 8px var(--ew-nav-glow, rgba(95, 234, 255, 0.18)), 0 6px 12px rgba(0,0,0,0.25); }
}
.ew-nav-btn:disabled {
  opacity: 0.45;
  cursor: default;
  box-shadow: none;
  transform: none;
}
`;
  document.head.appendChild(style);
}

function createIcon(name) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  switch (name) {
    case 'zoom-in':
      svg.appendChild(iconPath('M12 6v12M6 12h12'));
      break;
    case 'zoom-out':
      svg.appendChild(iconPath('M6 12h12'));
      break;
    case 'fit':
      svg.appendChild(iconPath('M7 9V7h2'));
      svg.appendChild(iconPath('M15 7h2v2'));
      svg.appendChild(iconPath('M17 15v2h-2'));
      svg.appendChild(iconPath('M9 17H7v-2'));
      break;
    case 'arrow-up':
      svg.appendChild(iconPath('M12 6l-4 4'));
      svg.appendChild(iconPath('M12 6l4 4'));
      svg.appendChild(iconPath('M12 6v12'));
      break;
    case 'arrow-down':
      svg.appendChild(iconPath('M12 18l-4-4'));
      svg.appendChild(iconPath('M12 18l4-4'));
      svg.appendChild(iconPath('M12 6v12'));
      break;
    case 'arrow-left':
      svg.appendChild(iconPath('M6 12l4-4'));
      svg.appendChild(iconPath('M6 12l4 4'));
      svg.appendChild(iconPath('M6 12h12'));
      break;
    case 'arrow-right':
      svg.appendChild(iconPath('M18 12l-4-4'));
      svg.appendChild(iconPath('M18 12l-4 4'));
      svg.appendChild(iconPath('M6 12h12'));
      break;
    case 'center':
      svg.appendChild(iconCircle(12, 12, 2.5));
      svg.appendChild(iconPath('M12 5v2'));
      svg.appendChild(iconPath('M12 17v2'));
      svg.appendChild(iconPath('M5 12h2'));
      svg.appendChild(iconPath('M17 12h2'));
      break;
    case 'focus':
      svg.appendChild(iconPath('M7 9V7h2'));
      svg.appendChild(iconPath('M17 7h-2V5'));
      svg.appendChild(iconPath('M7 15v2h2'));
      svg.appendChild(iconPath('M15 19h2v-2'));
      svg.appendChild(iconCircle(12, 12, 2));
      break;
    case 'chevron-left':
      svg.appendChild(iconPath('M15 6l-6 6 6 6'));
      break;
    case 'chevron-right':
      svg.appendChild(iconPath('M9 6l6 6-6 6'));
      break;
    default:
      return null;
  }
  return svg;
}

function iconPath(d) {
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  return path;
}

function iconCircle(cx, cy, r) {
  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', String(cx));
  circle.setAttribute('cy', String(cy));
  circle.setAttribute('r', String(r));
  return circle;
}

function makeDivider() {
  const divider = document.createElement('div');
  divider.className = 'ew-nav-divider';
  return divider;
}

const DEFAULT_KEYMAP = {
  ArrowUp: { type: 'nav:dir', dir: 'up' },
  ArrowDown: { type: 'nav:dir', dir: 'down' },
  ArrowLeft: { type: 'nav:dir', dir: 'left' },
  ArrowRight: { type: 'nav:dir', dir: 'right' },
  Tab: { type: 'nav:next' },
  'Shift+Tab': { type: 'nav:prev' },
  '=': { type: 'zoom:in' },
  '+': { type: 'zoom:in' },
  '-': { type: 'zoom:out' },
  '_': { type: 'zoom:out' },
  '0': { type: 'fit' },
  f: { type: 'focus' },
  F: { type: 'focus' },
  Home: { type: 'center' }
};

const DIR_VECTORS = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 }
};

function toKeyId(e) {
  const parts = [];
  if (e.shiftKey) parts.push('Shift');
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.metaKey) parts.push('Meta');
  parts.push(e.key);
  return parts.length > 1 ? parts.join('+') : e.key;
}

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function pickDirectional(candidates, current, direction) {
  if (!candidates.length) return null;
  const cx = current.x + current.w / 2;
  const cy = current.y + current.h / 2;
  let best = null;
  let bestScore = Infinity;
  for (const node of candidates) {
    if (!node) continue;
    if (node.id === current.id) continue;
    const nx = node.x + node.w / 2;
    const ny = node.y + node.h / 2;
    const dx = nx - cx;
    const dy = ny - cy;
    const dist = Math.hypot(dx, dy);
    if (!dist) continue;
    const dot = (dx * direction.x + dy * direction.y) / dist;
    if (dot <= 0.15) continue;
    const score = dist * (1 + (1 - dot) * 2.5);
    if (score < bestScore) {
      bestScore = score;
      best = node;
    }
  }
  return best;
}
